import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/db/db';
import { defaultCompanySettings } from '@/domain/company-settings/defaults';
import type { Company, CompanySettings, FiscalYear } from '@/domain/types';
import { useSettings } from './useSettings';
import { todayIso } from '../format';

export interface CompanyContext {
  /** `undefined` while loading, `null` when no company is active. */
  company: Company | null | undefined;
  companySettings: CompanySettings | undefined;
  fiscalYears: FiscalYear[];
  /** The open fiscal year containing today, else the latest open one, else the latest. */
  currentFiscalYear: FiscalYear | undefined;
  loading: boolean;
}

export function pickCurrentFiscalYear(years: readonly FiscalYear[], today = todayIso()): FiscalYear | undefined {
  const sorted = [...years].sort((a, b) => a.startDate.localeCompare(b.startDate));
  const containing = sorted.find((fy) => fy.status === 'open' && fy.startDate <= today && today <= fy.endDate);
  if (containing) return containing;
  const open = sorted.filter((fy) => fy.status === 'open');
  return open[open.length - 1] ?? sorted[sorted.length - 1];
}

/** Active company with its settings and fiscal years, live. */
export function useCompany(): CompanyContext {
  const settings = useSettings();
  const companyId = settings?.activeCompanyId ?? null;
  const data = useLiveQuery(async () => {
    if (settings === undefined) return undefined;
    if (!companyId) return { company: null, companySettings: undefined, fiscalYears: [] as FiscalYear[] };
    const company = (await db.companies.get(companyId)) ?? null;
    if (!company) return { company: null, companySettings: undefined, fiscalYears: [] as FiscalYear[] };
    const [stored, fiscalYears] = await Promise.all([
      db.companySettings.get(companyId),
      db.fiscalYears.where('companyId').equals(companyId).sortBy('startDate'),
    ]);
    return { company, companySettings: stored ?? defaultCompanySettings(company), fiscalYears };
  }, [settings === undefined, companyId]);

  if (data === undefined) {
    return { company: undefined, companySettings: undefined, fiscalYears: [], currentFiscalYear: undefined, loading: true };
  }
  return {
    company: data.company,
    companySettings: data.companySettings,
    fiscalYears: data.fiscalYears,
    currentFiscalYear: pickCurrentFiscalYear(data.fiscalYears),
    loading: false,
  };
}

/** All companies, for the switcher. */
export function useCompanies(): Company[] | undefined {
  return useLiveQuery(() => db.companies.orderBy('name').toArray(), []);
}
